// frontend/src/navigation/navItems.jsx
import {
  Home, ClipboardList, Undo2, Inbox, BarChart3, Package, GitBranch, Building2,
  LineChart, Rss, Truck, Warehouse, Shield, Globe, Activity, BookOpen, Film,
  Wallet, Timer, AlertTriangle, Ship, FileText, FileCheck, CalendarClock, Receipt, CalendarCheck,
} from 'lucide-react'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import { FS } from '../theme/tokens'

/**
 * navItems — EIN Vokabular fuer die Navigation (W-17).
 *
 * @description
 *   Sidebar und Mobile-Sheet trugen ihre Eintraege je zweimal: 26 Pfade, jedes
 *   Mal mit eigenem Label und eigenem Icon. Die Labels stimmten ueberein, die
 *   Icons nicht ganz — `/vendors/reports` trug in der Sidebar `SummarizeIcon`
 *   (MUI) und mobil `FileText` (Lucide). Genau so entsteht Drift: zwei Listen,
 *   die dasselbe meinen.
 *
 *   Hier steht jeder Eintrag EINMAL. Was die beiden Flaechen weiterhin selbst
 *   entscheiden, ist die GRUPPIERUNG — und die unterscheidet sich zu Recht:
 *   die Sidebar hat einen Abschnitt `core` mit Home und Purchase Orders, das
 *   Mobile-Sheet beginnt mit `Purchasing` und laesst beide weg. Ein Eintrag ist
 *   ein Eintrag; in welchem Abschnitt er steht, ist eine Frage der Flaeche.
 *
 *   🔴 Was hier NICHT steht: die Abschnitte, die Titel und die Reihenfolge.
 *   Die bleiben bei den Flaechen, weil sie sich wirklich unterscheiden.
 *
 * @useWhen
 *   - Ein Navigationseintrag in Sidebar, Mobile-Sheet oder einem kuenftigen
 *     dritten Ort (Befehlspalette W-20) gebraucht wird
 *
 * @dontUseFor
 *   - Erzeugende Aktionen (`CREATE_ITEMS` im Mobile-Sheet): die haben kein
 *     Ziel, sondern einen `action`-Namen, und sind keine Navigation
 */

/** Lucide-Icons tragen Groesse und Strichstaerke als Props, MUI ueber `sx`. */
const L = (C) => <C size={18} strokeWidth={1.75} />

/**
 * Pfad -> { label, icon }.
 *
 * 🔴 Ein neuer Eintrag gehoert HIERHIN, nicht in eine der beiden Flaechen.
 * Wer ihn dort eintraegt, baut die zweite Liste wieder auf, die dieser
 * Katalog abgeschafft hat.
 */
export const NAV_ITEMS = {
  '/':                        { label: 'Home',                icon: L(Home) },
  '/overview':                { label: 'Purchase Orders',     icon: L(ClipboardList) },
  '/po-inbox':                { label: 'Procurement Inbox',   icon: L(Inbox) },
  '/ops/po-anomalies':        { label: 'PO Anomalies',        icon: L(AlertTriangle) },
  '/ops/dep-health':          { label: 'Dependency Health',   icon: L(Activity) },
  '/ops/lsm-watcher':         { label: 'LSM Watcher',         icon: L(Activity) },
  '/rma':                     { label: 'Cases',               icon: L(Undo2) },
  '/rma/inbox':               { label: 'Inbox',               icon: L(Inbox) },
  '/rma/reports':             { label: 'Reports',             icon: L(BarChart3) },
  '/rma/sla-overview':        { label: 'SLAs',                icon: L(Timer) },
  // Von master nach dem Katalog gebaut (Merge 30.09.). Das Ziel stand dort nur in
  // der Leiste; ohne diesen Eintrag waere es beim Zusammenfuehren verschwunden.
  '/rma/faelligkeit':         { label: 'Due',                 icon: L(CalendarClock) },
  '/rma/analytics/inquiry-products': { label: 'Inquiry Analytics', icon: L(BarChart3) },
  '/rma/inquiry-knowledge':   { label: 'Knowledge',           icon: L(BookOpen) },
  '/reports/refunds':         { label: 'Refunds',             icon: L(Wallet) },
  '/products':                { label: 'Products',            icon: L(Package) },
  '/taxonomy':                { label: 'Taxonomy Hub',        icon: L(GitBranch) },
  '/vendors':                 { label: 'Vendors',             icon: L(Building2) },
  '/media':                   { label: 'Media Library',       icon: L(Film) },
  '/intelligence/market-lab': { label: 'Market Lab',          icon: L(Activity) },
  '/competitors':             { label: 'Competitors',         icon: L(LineChart) },
  '/discovery':               { label: 'Discovery Inbox',     icon: <ManageSearchIcon sx={{ fontSize: FS.xl }} /> },
  '/blog-sources':            { label: 'Blog Sources',        icon: L(Rss) },
  '/scrapers':                { label: 'Scrapers',            icon: L(Globe) },
  // 🔴 W-17: trug in der Sidebar `SummarizeIcon` (MUI) und mobil `FileText`
  // (Lucide). Beide Flaechen meinten dieselbe Sache und zeigten zwei Symbole.
  // Gewaehlt: `FileText` — Lucide ist das System der uebrigen 27 Eintraege.
  '/vendors/reports':         { label: 'Vendor Reports',      icon: L(FileText) },
  '/fulfillment':             { label: 'Fulfillment',         icon: L(Truck) },
  '/fca':                     { label: 'Full Cost',           icon: L(Wallet) },
  '/incoming-inventory':      { label: 'Incoming Inventory',  icon: L(Warehouse) },
  '/kn-shipments':            { label: 'K&N Shipments',       icon: L(Ship) },
  '/invoicing':               { label: 'Customer Invoices',   icon: L(Receipt) }, '/monatsabschluss': { label: 'Month-end Close', icon: L(CalendarCheck) },
  // Meldungsregister Stufe 1 (Aufgabe 9): die Uebersicht des Registers.
  // Die Seite selbst ist eine Lese-Flaeche — kein Knopf loest eine Meldung aus.
  '/declarations':            { label: 'Declarations',        icon: L(FileCheck) },
  '/admin/privacy':           { label: 'GDPR Requests',       icon: L(Shield) },
}

/**
 * Der Eintrag zu einem Pfad.
 *
 * 🔴 Wirft bei einem unbekannten Pfad, statt still `undefined` zu liefern. Ein
 * Nav-Eintrag ohne Label oder Icon rendert als leere Zeile, und das faellt
 * niemandem auf. Ein Tippfehler im Pfad soll beim Aufbau knallen, nicht in
 * einer unsichtbaren Luecke enden.
 */
export function navItem(path) {
  const eintrag = NAV_ITEMS[path]
  if (!eintrag) {
    throw new Error(
      `navItem('${path}'): kein Eintrag im Katalog. Neuen Pfad in ` +
      `frontend/src/navigation/navItems.jsx eintragen — nicht in der Flaeche.`,
    )
  }
  return { ...eintrag, path }
}

/**
 * Der Eintrag zu einem Pfad — oder `null`. Fuer NUTZERDATEN.
 *
 * 🔴 Der Unterschied zu `navItem` ist der GELTUNGSBEREICH, nicht die Bequemlichkeit.
 *   `navItem` liest QUELLTEXT: die Abschnitte der Seitenleiste stehen im Repo, ein
 *   Tippfehler soll beim Aufbau knallen. `navItemSicher` liest NUTZERDATEN: die
 *   Favoritenliste kommt aus `localStorage`, und ein Pfad, den die Verwaltung
 *   spaeter entfernt, darf die ganze Leiste nicht in den Error-Boundary reissen.
 *   Deshalb stehen die zwei Funktionen NEBENEINANDER und nicht als eine mit Flag.
 */
export function navItemSicher(path) {
  const eintrag = NAV_ITEMS[path]
  return eintrag ? { ...eintrag, path } : null
}

/**
 * istAktuelleSeite — welcher Eintrag einer Navigationsflaeche ist „die aktuelle Seite"?
 *
 * R16-A C-1 / R16-B C1 (Wiedervorlage PR 1). Die sichtbare Aktiv-Marke beider
 * Flaechen ist ein PRAEFIX-Vergleich: auf `/rma/inbox` sind `/rma` UND
 * `/rma/inbox` aktiv, und Eltern und Kind leuchten zu Recht beide.
 * `aria-current="page"` benennt dagegen GENAU EINEN Eintrag — zwei Traeger sagen
 * einem Screenreader zweimal „aktuelle Seite" an.
 *
 * Deshalb gewinnt hier der LAENGSTE passende Pfad; der exakte Treffer schlaegt
 * jeden Praefix. `kandidaten` sind die Pfade, die die Flaeche TATSAECHLICH
 * rendert (die Sidebar uebergibt die sichtbaren nach Voreinstellung, das mobile
 * Blatt seine Literalliste) — ein ausgeblendetes Kind gibt die Markierung damit
 * an den sichtbaren Eltern weiter, statt sie verschwinden zu lassen.
 *
 * Die sichtbare Markierung bleibt unberuehrt; hier geht es allein um das Attribut.
 */
export function istAktuelleSeite(path, pfadname, kandidaten) {
  let bester = null
  for (const p of kandidaten) {
    const trifft = p === '/'
      ? pfadname === '/'
      : pfadname === p || pfadname.startsWith(`${p}/`)
    if (trifft && (bester === null || p.length > bester.length)) bester = p
  }
  return bester === path
}

/**
 * ariaCurrentTraeger — wer traegt `aria-current="page"`, wenn es eine KOPIE gibt?
 *
 * 🔴 Ein angehefteter Eintrag wird ZWEIMAL gerendert: in der Favoritenliste und in
 *   seinem Abschnitt. Beide tragen denselben `path`, beide erfuellten
 *   `istAktuelleSeite` — und ein Screenreader hoerte zweimal „aktuelle Seite",
 *   genau das, was die Regel darueber („GENAU EINEN Traeger") verbietet.
 *
 * Die Traegerregel (Plan §3.4, B4):
 *
 *   | Lage                                                              | Traeger        |
 *   |-------------------------------------------------------------------|----------------|
 *   | Heimatzeile gerendert (nicht ausgeblendet ODER Modus)              | die Heimatzeile|
 *   | Heimatzeile NICHT gerendert (ausserhalb ausgeblendet, angeheftet)  | die Kopie      |
 *
 * 🔴 `kandidaten` sind die TATSAECHLICH gerenderten Pfade — und die Menge ist im
 *   Modus eine ANDERE als ausserhalb: im Modus steht ein ausgeblendeter Eintrag
 *   an seinem Platz, also sind es ALLE Eintraege (`sortiereNachPrefs(...)`),
 *   ausserhalb nur die sichtbaren (`sichtbarePfade(...)`). Waeren es im Modus die
 *   sichtbaren, waere die Zelle „oder Modus" traegerlos (Plan §3.4, N3).
 *
 * @param {string} path          der Pfad, fuer den gefragt wird
 * @param {string} pfadname      die aktuelle Route
 * @param {string[]} kandidaten  die tatsaechlich gerenderten Pfade
 * @param {object} [lage]
 * @param {boolean} [lage.istKopie]        wird die Zeile als Favoriten-Kopie gerendert?
 * @param {boolean} [lage.heimatGerendert] ist die Heimatzeile derselben Seite gerendert?
 */
export function ariaCurrentTraeger(path, pfadname, kandidaten, lage = {}) {
  const { istKopie = false, heimatGerendert = true } = lage
  if (istKopie && heimatGerendert) return false
  return istAktuelleSeite(path, pfadname, kandidaten)
}
